import React, { useState } from 'react';
import {
  updateLead,
  deleteLead,
  callDraftOutreachEmail,
  getLeadEmailHistory,
  deleteOutreachEmail,
  updateOutreachEmail,
  callSendOutreachEmail
} from '../../services/contentOpsService';

const ROLE_COLORS = {
  coach: 'bg-green-600/20 text-green-400',
  tournament_organizer: 'bg-orange-600/20 text-orange-400',
  creator: 'bg-pink-600/20 text-pink-400',
  club_owner: 'bg-cyan-600/20 text-cyan-400',
  player: 'bg-blue-600/20 text-blue-400',
  developer: 'bg-purple-600/20 text-purple-400',
  journalist: 'bg-yellow-600/20 text-yellow-400',
  person: 'bg-gray-600/20 text-gray-400',
  other: 'bg-gray-600/20 text-gray-400'
};

const STATUS_COLORS = {
  new: 'bg-blue-600/20 text-blue-400',
  researched: 'bg-yellow-600/20 text-yellow-400',
  email_drafted: 'bg-purple-600/20 text-purple-400',
  contacted: 'bg-green-600/20 text-green-400',
  followed_up: 'bg-teal-600/20 text-teal-400',
  converted: 'bg-emerald-600/20 text-emerald-400',
  not_interested: 'bg-red-600/20 text-red-400',
  no_response: 'bg-gray-600/20 text-gray-400'
};

const EMAIL_STATUS_COLORS = {
  draft: 'bg-yellow-600/20 text-yellow-400',
  approved: 'bg-green-600/20 text-green-400',
  sent: 'bg-blue-600/20 text-blue-400',
  rejected: 'bg-red-600/20 text-red-400'
};

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const LeadCard = ({ lead, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [emails, setEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [copiedLeadProfile, setCopiedLeadProfile] = useState(false);
  const [copiedEmailId, setCopiedEmailId] = useState(null);

  // Editable lead fields
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editLink, setEditLink] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [savingLead, setSavingLead] = useState(false);

  // Email inline editing
  const [expandedEmailId, setExpandedEmailId] = useState(null);
  const [emailEdits, setEmailEdits] = useState({});
  const [savingEmailId, setSavingEmailId] = useState(null);
  const [sendingEmailId, setSendingEmailId] = useState(null);

  const handleExpand = async () => {
    if (!expanded) {
      // Initialize editable fields from lead
      setEditName(lead.name || lead.widgetTitle || '');
      setEditEmail(lead.contactEmail || '');
      setEditLink(lead.link || '');
      setEditNotes(lead.notes || '');
      setExpandedEmailId(null);
      setEmailEdits({});

      setLoadingEmails(true);
      try {
        const history = await getLeadEmailHistory(lead.id);
        setEmails(history);
      } catch (err) {
        console.error('Failed to load email history:', err);
      } finally {
        setLoadingEmails(false);
      }
    }
    setExpanded(!expanded);
  };

  const handleSaveLead = async () => {
    setSavingLead(true);
    try {
      const updates = {};
      if (editName !== (lead.name || lead.widgetTitle || '')) updates.name = editName;
      if (editEmail !== (lead.contactEmail || '')) updates.contactEmail = editEmail;
      if (editLink !== (lead.link || '')) updates.link = editLink;
      if (editNotes !== (lead.notes || '')) updates.notes = editNotes;
      await updateLead(lead.id, updates);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Save lead error:', err);
    } finally {
      setSavingLead(false);
    }
  };

  const handleDeleteLead = async () => {
    if (!window.confirm(`Delete lead "${lead.name || lead.widgetTitle}"? This cannot be undone.`)) return;
    try {
      await deleteLead(lead.id);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Delete lead error:', err);
    }
  };

  const handleDeleteEmail = async (emailId) => {
    if (!window.confirm('Delete this email draft?')) return;
    try {
      await deleteOutreachEmail(emailId);
      setEmails(prev => prev.filter(e => e.id !== emailId));
      if (expandedEmailId === emailId) setExpandedEmailId(null);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Delete email error:', err);
    }
  };

  const handleDraftEmail = async (template) => {
    try {
      setDrafting(true);
      await callDraftOutreachEmail(lead.id, template);
      const history = await getLeadEmailHistory(lead.id);
      setEmails(history);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Draft email error:', err);
    } finally {
      setDrafting(false);
    }
  };

  const handleToggleEmail = (emailId) => {
    if (expandedEmailId === emailId) {
      setExpandedEmailId(null);
      return;
    }
    // Initialize edits for this email
    const email = emails.find(e => e.id === emailId);
    if (email) {
      setEmailEdits({
        subject: email.subject || '',
        body: email.body || ''
      });
    }
    setExpandedEmailId(emailId);
  };

  const handleSaveEmailEdit = async (emailId) => {
    setSavingEmailId(emailId);
    try {
      await updateOutreachEmail(emailId, {
        subject: emailEdits.subject,
        body: emailEdits.body
      });
      setEmails(prev => prev.map(e =>
        e.id === emailId ? { ...e, subject: emailEdits.subject, body: emailEdits.body } : e
      ));
    } catch (err) {
      console.error('Save email error:', err);
    } finally {
      setSavingEmailId(null);
    }
  };

  const handleApproveEmail = async (emailId) => {
    setSavingEmailId(emailId);
    try {
      await updateOutreachEmail(emailId, { status: 'approved' });
      setEmails(prev => prev.map(e =>
        e.id === emailId ? { ...e, status: 'approved' } : e
      ));
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Approve email error:', err);
    } finally {
      setSavingEmailId(null);
    }
  };

  const handleRejectEmail = async (emailId) => {
    setSavingEmailId(emailId);
    try {
      await updateOutreachEmail(emailId, { status: 'rejected' });
      setEmails(prev => prev.map(e =>
        e.id === emailId ? { ...e, status: 'rejected' } : e
      ));
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Reject email error:', err);
    } finally {
      setSavingEmailId(null);
    }
  };

  const handleSendEmail = async (emailId) => {
    if (!lead.contactEmail) {
      alert('This lead has no contact email saved in the database. Save a contact email first.');
      return;
    }
    if (!window.confirm(`Send this email to ${lead.contactEmail}?`)) return;
    setSendingEmailId(emailId);
    try {
      await callSendOutreachEmail(emailId);
      setEmails(prev => prev.map(e =>
        e.id === emailId ? { ...e, status: 'sent' } : e
      ));
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Send email error:', err);
    } finally {
      setSendingEmailId(null);
    }
  };

  const formatSocialLinks = () => {
    if (!lead.links || Object.keys(lead.links).length === 0) return 'none';
    return Object.entries(lead.links).map(([k, v]) => `${k}: ${v}`).join(', ');
  };

  const handleCopyLeadProfile = () => {
    const text = `Run /create-profile for this outreach lead.

Lead ID: ${lead.id}
Name: ${lead.name || lead.widgetTitle || ''}
Role: ${lead.role || 'unknown'}
Email: ${lead.contactEmail || 'none'}
Website: ${lead.link || 'none'}
Social links: ${formatSocialLinks()}
Lead score: ${lead.leadScore ?? 'unknown'}
Suggested angle: ${lead.suggestedAngle || 'none'}
Notes: ${lead.notes || 'none'}

After publishing, update lead doc "${lead.id}" in Firestore collection outreach_leads (project: indie-chess) with profileId, profileUrl, claimUrl, and profileCreatedAt.`;

    navigator.clipboard.writeText(text);
    setCopiedLeadProfile(true);
    setTimeout(() => setCopiedLeadProfile(false), 2000);
  };

  const handleCopyEmailProfile = (email) => {
    const text = `Run /create-profile for this outreach lead, then update the email draft.

Lead ID: ${lead.id}
Name: ${lead.name || lead.widgetTitle || ''}
Role: ${lead.role || 'unknown'}
Email: ${lead.contactEmail || 'none'}
Website: ${lead.link || 'none'}
Social links: ${formatSocialLinks()}
Lead score: ${lead.leadScore ?? 'unknown'}
Suggested angle: ${lead.suggestedAngle || 'none'}
Notes: ${lead.notes || 'none'}

Current email draft (ID: ${email.id}):
Subject: ${email.subject || ''}
Body:
${email.body || ''}

After creating and publishing the profile:
1. Update lead doc "${lead.id}" in outreach_leads (project: indie-chess) with profileId, profileUrl, claimUrl, profileCreatedAt
2. Rewrite email draft "${email.id}" in outreach_emails — naturally weave in the profile URL, mention they can connect their own .com domain. Keep the same tone and approach, just add the website reference.`;

    navigator.clipboard.writeText(text);
    setCopiedEmailId(email.id);
    setTimeout(() => setCopiedEmailId(null), 2000);
  };

  // Dirty check — show save button only when edits differ from stored values
  const leadDirty =
    editName !== (lead.name || lead.widgetTitle || '') ||
    editEmail !== (lead.contactEmail || '') ||
    editLink !== (lead.link || '') ||
    editNotes !== (lead.notes || '');

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
      {/* Card Header */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-750 transition-colors"
        onClick={handleExpand}
      >
        <div className="flex justify-between items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-gray-200 font-medium text-sm">
                {lead.name || lead.widgetTitle || '—'}
              </h4>
              {lead.role && (
                <span className={`text-xs px-2 py-0.5 rounded ${ROLE_COLORS[lead.role] || ROLE_COLORS.other}`}>
                  {lead.role.replace('_', ' ')}
                </span>
              )}
              <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[lead.status] || 'bg-gray-700 text-gray-400'}`}>
                {lead.status}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
              {lead.profileUrl && (
                <a
                  href={lead.profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-400 hover:text-emerald-300 font-medium"
                  onClick={e => e.stopPropagation()}
                >
                  ChessWeb Profile
                </a>
              )}
              {lead.contactEmail && <span>{lead.contactEmail}</span>}
              {lead.link && (
                <a
                  href={lead.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 truncate max-w-[200px]"
                  onClick={e => e.stopPropagation()}
                >
                  {lead.link}
                </a>
              )}
              {lead.country && <span>{lead.country}</span>}
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {lead.leadScore != null && (
              <div className="text-center">
                <div className={`text-lg font-bold ${
                  lead.leadScore >= 7 ? 'text-green-400' :
                  lead.leadScore >= 4 ? 'text-yellow-400' :
                  'text-gray-500'
                }`}>
                  {lead.leadScore}
                </div>
                <div className="text-xs text-gray-500">score</div>
              </div>
            )}
            {lead.bestApproach && (
              <span className="text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-300">
                {lead.bestApproach === 'chessweb_pitch' ? 'Pitch' : 'Directory'}
              </span>
            )}
          </div>
        </div>

        {lead.useCase && (
          <div className="mt-2 text-xs text-gray-400">
            Use case: {lead.useCase.replace('_', ' ')}
          </div>
        )}

        {lead.suggestedAngle && (
          <p className="mt-1 text-xs text-gray-500 italic line-clamp-2">{lead.suggestedAngle}</p>
        )}
      </div>

      {/* Expanded Section */}
      {expanded && (
        <div className="border-t border-gray-700 p-4 space-y-5">

          {/* Section 1: Lead Details */}
          <div>
            <h5 className="text-xs text-gray-400 font-medium mb-3 uppercase tracking-wide">Lead Details</h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Contact Email</label>
                <input
                  type="email"
                  value={editEmail}
                  onChange={e => setEditEmail(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Website / Link</label>
                <input
                  type="url"
                  value={editLink}
                  onChange={e => setEditLink(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-xs text-gray-500 mb-1">Notes</label>
              <textarea
                value={editNotes}
                onChange={e => setEditNotes(e.target.value)}
                rows={3}
                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none resize-y"
              />
            </div>
            {leadDirty && (
              <button
                onClick={handleSaveLead}
                disabled={savingLead}
                className="mt-3 bg-blue-600 hover:bg-blue-500 text-white px-4 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
              >
                {savingLead ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>

          {/* Section 2: Actions */}
          <div className="border-t border-gray-700 pt-4">
            <h5 className="text-xs text-gray-400 font-medium mb-3 uppercase tracking-wide">Actions</h5>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleDraftEmail('directory_listing')}
                disabled={drafting}
                className="bg-purple-600/20 hover:bg-purple-600/40 text-purple-400 px-3 py-1.5 rounded text-xs transition-colors disabled:opacity-50"
              >
                {drafting ? 'Drafting...' : 'Draft Directory Email'}
              </button>
              <button
                onClick={() => handleDraftEmail('chessweb_pitch')}
                disabled={drafting}
                className="bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-400 px-3 py-1.5 rounded text-xs transition-colors disabled:opacity-50"
              >
                {drafting ? 'Drafting...' : 'Draft ChessWeb Pitch'}
              </button>
              {!lead.profileId && (
                <button
                  onClick={handleCopyLeadProfile}
                  className="bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 px-3 py-1.5 rounded text-xs transition-colors"
                >
                  {copiedLeadProfile ? 'Copied!' : 'Build Website'}
                </button>
              )}
              {lead.profileUrl && (
                <a
                  href={lead.profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 px-3 py-1.5 rounded text-xs transition-colors inline-flex items-center"
                >
                  View Website
                </a>
              )}
              <button
                onClick={handleDeleteLead}
                className="bg-red-600/20 hover:bg-red-600/40 text-red-400 px-3 py-1.5 rounded text-xs transition-colors ml-auto"
              >
                Delete Lead
              </button>
            </div>
          </div>

          {/* Section 3: Email History */}
          <div className="border-t border-gray-700 pt-4">
            <h5 className="text-xs text-gray-400 font-medium mb-3 uppercase tracking-wide">
              Email History ({emails.length})
            </h5>
            {loadingEmails ? (
              <div className="flex justify-center py-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
              </div>
            ) : emails.length === 0 ? (
              <p className="text-xs text-gray-500">No emails yet.</p>
            ) : (
              <div className="space-y-2">
                {emails.map(email => {
                  const isExpanded = expandedEmailId === email.id;
                  const isSent = email.status === 'sent';
                  const isSaving = savingEmailId === email.id;
                  const isSending = sendingEmailId === email.id;

                  return (
                    <div key={email.id} className="bg-gray-900 border border-gray-700 rounded overflow-hidden">
                      {/* Email header row */}
                      <div
                        className="flex items-center gap-2 p-3 cursor-pointer hover:bg-gray-800/50 transition-colors"
                        onClick={() => handleToggleEmail(email.id)}
                      >
                        <span className="text-gray-500 text-xs">{isExpanded ? '▾' : '▸'}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${EMAIL_STATUS_COLORS[email.status] || 'bg-gray-700 text-gray-400'}`}>
                          {email.status}
                        </span>
                        <span className="text-xs text-gray-500">{email.template}</span>
                        <span className="text-xs text-gray-300 truncate flex-1">
                          {email.subject || '(no subject)'}
                        </span>
                        <span className="text-xs text-gray-500 shrink-0">{formatDate(email.createdAt)}</span>
                      </div>

                      {/* Expanded email body */}
                      {isExpanded && (
                        <div className="border-t border-gray-700 p-3 space-y-3">
                          {/* Subject */}
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Subject</label>
                            {isSent ? (
                              <p className="text-sm text-gray-300">{email.subject || '(no subject)'}</p>
                            ) : (
                              <input
                                type="text"
                                value={emailEdits.subject ?? ''}
                                onChange={e => setEmailEdits(prev => ({ ...prev, subject: e.target.value }))}
                                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                              />
                            )}
                          </div>

                          {/* Body */}
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Body</label>
                            {isSent ? (
                              <div className="whitespace-pre-wrap font-mono text-sm text-gray-300 bg-gray-800 rounded p-3 max-h-96 overflow-y-auto">
                                {email.body || '(empty)'}
                              </div>
                            ) : (
                              <textarea
                                value={emailEdits.body ?? ''}
                                onChange={e => setEmailEdits(prev => ({ ...prev, body: e.target.value }))}
                                rows={10}
                                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-200 font-mono whitespace-pre-wrap focus:border-blue-500 focus:outline-none resize-y"
                              />
                            )}
                          </div>

                          {/* Action buttons by status */}
                          <div className="flex gap-2 flex-wrap pt-1">
                            {email.status === 'draft' && (
                              <>
                                <button
                                  onClick={() => handleApproveEmail(email.id)}
                                  disabled={isSaving}
                                  className="bg-green-600/20 hover:bg-green-600/40 text-green-400 px-3 py-1.5 rounded text-xs transition-colors disabled:opacity-50"
                                >
                                  {isSaving ? '...' : 'Approve'}
                                </button>
                                <button
                                  onClick={() => handleRejectEmail(email.id)}
                                  disabled={isSaving}
                                  className="bg-red-600/20 hover:bg-red-600/40 text-red-400 px-3 py-1.5 rounded text-xs transition-colors disabled:opacity-50"
                                >
                                  Reject
                                </button>
                                <button
                                  onClick={() => handleSaveEmailEdit(email.id)}
                                  disabled={isSaving}
                                  className="bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 px-3 py-1.5 rounded text-xs transition-colors disabled:opacity-50"
                                >
                                  {isSaving ? 'Saving...' : 'Save Edit'}
                                </button>
                                {!lead.profileId && (
                                  <button
                                    onClick={() => handleCopyEmailProfile(email)}
                                    className="bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 px-3 py-1.5 rounded text-xs transition-colors"
                                  >
                                    {copiedEmailId === email.id ? 'Copied!' : 'Build Website'}
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteEmail(email.id)}
                                  className="bg-red-600/20 hover:bg-red-600/40 text-red-400 px-3 py-1.5 rounded text-xs transition-colors ml-auto"
                                >
                                  Delete
                                </button>
                              </>
                            )}
                            {email.status === 'approved' && (
                              <>
                                <button
                                  onClick={() => handleSendEmail(email.id)}
                                  disabled={isSending || !lead.contactEmail}
                                  title={!lead.contactEmail ? 'Save a contact email first' : ''}
                                  className="bg-green-600 hover:bg-green-500 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isSending ? 'Sending...' : 'Send'}
                                </button>
                                <button
                                  onClick={() => handleSaveEmailEdit(email.id)}
                                  disabled={isSaving}
                                  className="bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 px-3 py-1.5 rounded text-xs transition-colors disabled:opacity-50"
                                >
                                  {isSaving ? 'Saving...' : 'Save Edit'}
                                </button>
                                {!lead.profileId && (
                                  <button
                                    onClick={() => handleCopyEmailProfile(email)}
                                    className="bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 px-3 py-1.5 rounded text-xs transition-colors"
                                  >
                                    {copiedEmailId === email.id ? 'Copied!' : 'Build Website'}
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteEmail(email.id)}
                                  className="bg-red-600/20 hover:bg-red-600/40 text-red-400 px-3 py-1.5 rounded text-xs transition-colors ml-auto"
                                >
                                  Delete
                                </button>
                              </>
                            )}
                            {email.status === 'rejected' && (
                              <button
                                onClick={() => handleDeleteEmail(email.id)}
                                className="bg-red-600/20 hover:bg-red-600/40 text-red-400 px-3 py-1.5 rounded text-xs transition-colors"
                              >
                                Delete
                              </button>
                            )}
                            {/* sent = no action buttons, read-only view */}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadCard;
